import { supabase } from './supabase';
import { sendEmail } from './email';
import { logAdminAction } from './audit-log';

const BETA_MODE = import.meta.env.VITE_BETA_MODE === 'true';

export function isBetaMode(): boolean {
  return BETA_MODE;
}

export async function isBetaTester(email: string): Promise<boolean> {
  if (!BETA_MODE) return true;
  if (!email) return false;
  // Waitlist rows (notes='waitlist_signup') are pending approval, and
  // deactivated rows (notes='deactivated') are admin-revoked — neither counts.
  const { data } = await supabase
    .from('beta_testers')
    .select('id, notes')
    .eq('email', email.toLowerCase())
    .maybeSingle();
  if (!data) return false;
  return data.notes !== 'waitlist_signup' && data.notes !== 'deactivated';
}

export interface BetaTester {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
  notes: string | null;
  is_agency: boolean;
}

export async function getBetaTesters(): Promise<BetaTester[]> {
  const { data } = await supabase
    .from('beta_testers')
    .select('*')
    .order('created_at', { ascending: false });
  return (data || []) as BetaTester[];
}

export async function addBetaTester(
  email: string,
  name?: string,
  notes?: string,
  isAgency?: boolean,
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('beta_testers')
    .insert({
      email: email.toLowerCase(),
      name: name || null,
      notes: notes || null,
      is_agency: isAgency || false,
    });

  if (error) {
    if (error.code === '23505') return { success: false, error: 'Email already in whitelist' };
    return { success: false, error: error.message };
  }
  logAdminAction('beta_tester.add', { targetType: 'beta_tester', targetId: email.toLowerCase(), metadata: { name, isAgency: !!isAgency } });
  return { success: true };
}

export async function removeBetaTester(id: string): Promise<void> {
  await supabase.from('beta_testers').delete().eq('id', id);
  logAdminAction('beta_tester.remove', { targetType: 'beta_tester', targetId: id });
}

export async function activateBetaTester(id: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('beta_testers')
    .update({ notes: null })
    .eq('id', id);
  if (error) return { success: false, error: error.message };
  logAdminAction('beta_tester.activate', { targetType: 'beta_tester', targetId: id });
  return { success: true };
}

export async function deactivateBetaTester(id: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('beta_testers')
    .update({ notes: 'deactivated' })
    .eq('id', id);
  if (error) return { success: false, error: error.message };
  logAdminAction('beta_tester.deactivate', { targetType: 'beta_tester', targetId: id });
  return { success: true };
}

export async function sendBetaInviteEmail(email: string, name?: string): Promise<boolean> {
  const { data: row } = await supabase
    .from('beta_testers')
    .select('invite_token')
    .eq('email', email.toLowerCase())
    .maybeSingle();

  if (!row?.invite_token) return false;

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://japan.tripgenesys.com';
  const inviteUrl = `${origin}/beta-signup?token=${encodeURIComponent(row.invite_token)}`;

  return sendEmail({
    template: 'beta_invite',
    to: email,
    data: { name: name || '', invite_url: inviteUrl },
  });
}
